import React, { useCallback, useEffect, useState } from "react"
import Button from "@mui/material/Button"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import {
  Stack,
  Box,
  IconButton,
  Badge,
  Typography,
  Divider,
} from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import { staticRelativeImageHandler } from "../../utils/imageHandler"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"

interface PreviewFile {
  img: string
  isRemote: boolean
  id: string
  file?: File
}

interface SortableItemProps {
  imgObj: PreviewFile
  index: number
  onRemove: (id: string, isRemote: boolean) => void
}

const SortableItem: React.FC<SortableItemProps> = ({
  imgObj,
  index,
  onRemove,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: imgObj.id,
    })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <Stack
      ref={setNodeRef}
      sx={{
        position: "relative",
        borderColor: "grey.300",
        cursor: imgObj.isRemote ? "move" : "default",
        width: "calc(33.33% - 16px)",
        ...style,
      }}
      {...(imgObj.isRemote ? { ...attributes, ...listeners } : {})}
    >
      <Badge
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        badgeContent={index + 1}
        color="primary"
        sx={{
          display: "block",
          width: "100%",
          "& .MuiBadge-badge": {
            fontSize: "1rem",
            height: "24px",
            minWidth: "24px",
            borderRadius: "12px",
            color: "black",
          },
        }}
      >
        <img
          src={
            imgObj?.isRemote
              ? `${staticRelativeImageHandler(imgObj?.img)}`
              : imgObj?.img
          }
          alt={`Uploaded ${index + 1}`}
          height="97px"
          style={{
            objectFit: "contain",
            width: "100%",
            display: "block",
          }}
        />
      </Badge>
      <IconButton
        className="delete-button"
        size="small"
        onMouseDown={(e) => {
          e.stopPropagation()
          onRemove(imgObj?.id, imgObj?.isRemote)
        }}
        sx={{
          position: "absolute",
          top: 3,
          right: 3,
          backgroundColor: "white",
          zIndex: 10,
          "&:hover": {
            backgroundColor: "grey.100",
          },
        }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Stack>
  )
}

type FileUploadDndProps = {
  selectedFiles: File[]
  setSelectedFiles: React.Dispatch<React.SetStateAction<File[]>>
  remoteImages: string[]
  setRemoteImages: React.Dispatch<React.SetStateAction<string[]>>
}

const FileUploadDnd: React.FC<FileUploadDndProps> = ({
  selectedFiles,
  setSelectedFiles,
  remoteImages,
  setRemoteImages,
}) => {
  const [localPreviewFiles, setLocalPreviewFiles] = useState<PreviewFile[]>([])
  const [remotePreviewFiles, setRemotePreviewFiles] = useState<PreviewFile[]>(
    [],
  )

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(event.target.files || [])
    const newUploadedFiles: File[] = []

    for (const newFile of newFiles) {
      const isDuplicate = selectedFiles?.some(
        (selectedFile) =>
          selectedFile.name === newFile.name &&
          selectedFile.size === newFile.size,
      )

      if (!isDuplicate) {
        newUploadedFiles.push(newFile)
      }
    }
    setSelectedFiles((prevFiles) => [...prevFiles, ...newUploadedFiles])
    event.target.value = ""
  }

  const generatePreviewId = useCallback(() => {
    return `file-${Math.random().toString(36).slice(2, 11)}`
  }, [])

  const updatePreviewFiles = useCallback(() => {
    // Update local files
    setLocalPreviewFiles((prevFiles) => {
      const localFileMap = new Map(
        selectedFiles.map((file) => [file.name + file.size, file]),
      )

      // Filter out files that no longer exist
      const existingFiles = prevFiles.filter(
        (file) =>
          file.file && localFileMap.has(file.file.name + file.file.size),
      )

      // Create map for existing files
      const existingLocalMap = new Map(
        existingFiles
          .filter((f) => f.file !== undefined)
          .map((f) => [f.file ? f.file.name + f.file.size : "", f]),
      )

      // Create array for new files
      const newLocalFiles = selectedFiles
        .filter((f) => {
          const key = f.name + f.size
          return !existingLocalMap.has(key)
        })
        .map((file) => ({
          img: URL.createObjectURL(file),
          isRemote: false,
          id: generatePreviewId(),
          file,
        }))

      const finalFiles = [...existingFiles, ...newLocalFiles]

      // Clean up unused object URLs
      prevFiles.forEach((oldFile) => {
        if (!finalFiles.includes(oldFile)) {
          URL.revokeObjectURL(oldFile.img)
        }
      })

      return finalFiles
    })

    // Update remote files
    setRemotePreviewFiles((prevFiles) => {
      const remoteFileMap = new Map(remoteImages.map((url) => [url, url]))

      // Filter out files that no longer exist
      const existingFiles = prevFiles.filter((file) =>
        remoteFileMap.has(file.img),
      )

      // Create map for existing files
      const existingRemoteMap = new Map(existingFiles.map((f) => [f.img, f]))

      // Create array for new files
      const newRemoteFiles = remoteImages
        .filter((url) => !existingRemoteMap.has(url))
        .map((url) => ({
          img: url,
          isRemote: true,
          id: generatePreviewId(),
        }))

      return [...existingFiles, ...newRemoteFiles]
    })
  }, [selectedFiles, remoteImages, generatePreviewId])

  const handleRemove = (id: string, isRemote: boolean) => {
    if (isRemote) {
      setRemotePreviewFiles((prevFiles) => {
        const fileIndex = prevFiles.findIndex((file) => file.id === id)
        if (fileIndex === -1) return prevFiles

        const file = prevFiles[fileIndex]
        const newFiles = [...prevFiles]
        newFiles.splice(fileIndex, 1)

        const newRemoteImages = remoteImages.filter((img) => img !== file.img)
        setRemoteImages(newRemoteImages)

        return newFiles
      })
    } else {
      setLocalPreviewFiles((prevFiles) => {
        const fileIndex = prevFiles.findIndex((file) => file.id === id)
        if (fileIndex === -1) return prevFiles

        const file = prevFiles[fileIndex]
        const newFiles = [...prevFiles]
        newFiles.splice(fileIndex, 1)

        URL.revokeObjectURL(file.img)

        if (file.file) {
          const newLocalFiles = selectedFiles.filter(
            (f) => f.name !== file.file?.name || f.size !== file.file?.size,
          )
          setSelectedFiles(newLocalFiles)
        }

        return newFiles
      })
    }
  }

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (active?.id !== over?.id && over?.id) {
      setRemotePreviewFiles((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id)
        const newIndex = items.findIndex((item) => item.id === over.id)

        if (oldIndex === -1 || newIndex === -1) return items

        const newItems = arrayMove(items, oldIndex, newIndex)
        setRemoteImages(newItems.map((item) => item.img))
        return newItems
      })
    }
  }

  useEffect(() => {
    updatePreviewFiles()
  }, [updatePreviewFiles])

  return (
    <Stack spacing={3}>
      <div>
        <Typography variant="h6" gutterBottom>
          New Images
        </Typography>
        <input
          accept="image/*"
          style={{ display: "none" }}
          id="new-file-upload-input"
          type="file"
          multiple
          onChange={handleFileChange}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            width: "100%",
            gap: 3,
            mt: 2,
            justifyContent: "flex-start",
          }}
        >
          {localPreviewFiles.map((imgObj, index) => (
            <SortableItem
              key={imgObj.id}
              imgObj={imgObj}
              index={index}
              onRemove={handleRemove}
            />
          ))}
        </Box>
        <label htmlFor="new-file-upload-input">
          <Button
            style={{ width: "100%", marginBottom: "10px", marginTop: "10px" }}
            variant="contained"
            color="primary"
            component="span"
            startIcon={<CloudUploadIcon />}
          >
            Upload New Files
          </Button>
        </label>
        Upload new images here. Rearrange them in the existing images section
        after saving.
      </div>
      {remotePreviewFiles.length > 0 && (
        <>
          <Divider />

          <div>
            <Typography variant="h6" gutterBottom>
              Delete / Rearrange Existing Images
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                width: "100%",
                gap: 3,
                mt: 2,
                justifyContent: "flex-start",
              }}
            >
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={(e) => handleDragEnd(e)}
              >
                <SortableContext
                  items={remotePreviewFiles.map((item) => item.id)}
                  strategy={horizontalListSortingStrategy}
                >
                  {remotePreviewFiles.map((imgObj, index) => (
                    <SortableItem
                      key={imgObj.id}
                      imgObj={imgObj}
                      index={index}
                      onRemove={handleRemove}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </Box>
          </div>
        </>
      )}
    </Stack>
  )
}

export default FileUploadDnd
